import React, { useEffect, useState, useRef } from "react";
import "./AnimatedGallery.css";
import axios from "axios";

const config = require("../config.json");

const EFFECTS = [
  "effect-fade",
  "effect-zoom-in",
  "effect-zoom-out",
  "effect-slide-left",
  "effect-slide-right",
  "effect-slide-top",
  "effect-slide-bottom",
  "effect-flip-h",
  "effect-flip-v",
  "effect-diamond",
  "effect-explode",
  "effect-shutter",
  "effect-push",
  "effect-wipe",
  "effect-curtain",
  "effect-blur",
];

function getRandomEffect() {
  return EFFECTS[Math.floor(Math.random() * EFFECTS.length)];
}

export default function SliderViewer({ interval = 3500 }) {
  const [index, setIndex] = useState(0);
  const [effect, setEffect] = useState(getRandomEffect());
  const timeoutRef = useRef(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // -------------------------------
  // Fetch gallery images
  // -------------------------------
  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      setLoaded(false);

      const fileName = "images/slider";
      const bucketName = "images.stxaviersthoothukudi.com";

      const response = await axios.get(
        `${config.api.invokeURL}/dashboard/website/slider?fileName=${fileName}&bucketName=${bucketName}`,
        {
          headers: { "x-api-key": config.api.key },
        }
      );

      if (response.data?.length > 0) {
        const shuffled = shuffleArray(response.data);
        setGalleryImages(shuffled);
      }

      setLoaded(true);
    } catch (err) {
      console.error("Error fetching images:", err);
      setLoaded(true);
    }
  };

  // Shuffle utility
  function shuffleArray(arr) {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  // -------------------------------
  // Slider Timer Logic
  // -------------------------------
  useEffect(() => {
    if (galleryImages.length === 0) return;

    startTimer();
    return () => stopTimer();
  }, [index, galleryImages]);

  function startTimer() {
    stopTimer();
    timeoutRef.current = setTimeout(() => {
      setEffect(getRandomEffect());
      setIndex((prev) => (prev + 1) % galleryImages.length);
    }, interval);
  }

  function stopTimer() {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }

  // Controls
  const goTo = (i) => {
    stopTimer();
    setEffect(getRandomEffect());
    setIndex(i % galleryImages.length);
  };

  const prev = () => {
    stopTimer();
    setEffect(getRandomEffect());
    setIndex((prevIdx) => (prevIdx - 1 + galleryImages.length) % galleryImages.length);
  };

  const next = () => {
    stopTimer();
    setEffect(getRandomEffect());
    setIndex((prevIdx) => (prevIdx + 1) % galleryImages.length);
  };

  // -------------------------------
  // UI Rendering
  // -------------------------------
  return (
    <section id="gallery" className="container-fluid p-0">
      <div className="ag-container animated-gallery">
        {loaded &&
          galleryImages.map((src, i) => {
            const finalURL = `${config.s3.imageUrl}${src}?t=${Date.now()}`;

            return (
              <React.Fragment key={i}>
                <div
                  className={`ag-slide ${i === index ? `active ${effect}` : ""}`}
                  style={{
                    backgroundImage: `url("${finalURL}")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                  aria-hidden={i !== index}
                />
              </React.Fragment>
            );
          })}

        {/* Navigation Arrows */}
        <button className="ag-arrow left" onClick={prev} aria-label="Previous slide">
          ‹
        </button>
        <button className="ag-arrow right" onClick={next} aria-label="Next slide">
          ›
        </button>

        {/* Dots */}
        <div className="ag-controls" role="tablist">
          {galleryImages.map((_, i) => (
            <div
              key={i}
              role="tab"
              aria-selected={i === index}
              tabIndex={0}
              className={`ag-dot ${i === index ? "active" : ""}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
